import React, { useMemo } from 'react'
import { AppBar, Container, Grid, Toolbar } from '@mui/material'
import Box from '@mui/material/Box'
import { GameImageFilter, GameINFOV2, LeagueItem, PartnerPageData } from 'types/types'
import Link from 'next/link'
import { useMiniLeagueStore, useParentGameStore } from '@/store/store'
import { useAuth } from 'context/JWTContext'
import { getBgImageBySport, getFirstUpcomingRound } from 'types/utils'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useRouter } from 'next/router'
import { HeaderHomeButton } from 'components/shared/HeaderHomeButton'
import { ProfileDrawer } from 'components/prize-draws/profile-drawer/ProfileDrawer'
import { isTBLPage } from '@/utils/tblUtils'
import LoginButton from './LoginButton'
import SectionSwitcher from './SectionSwitch'
import LeagueCarousel from './LeagueCarousel'
import LoggedOutHeaderMenu from './LoggedOutHeaderMenu'
import GameInfoCard from './GameInfoCard'

interface DynamicHeaderProps {
  pageTheme: PartnerPageData & { is_user_joined: boolean; game?: GameINFOV2 }
  isMiniLeague?: boolean
  leagues?: LeagueItem[]
  shouldShowBox: boolean
  shouldShowLeagueDropdown?: boolean
  game?: GameINFOV2 | null
}

// TODO: temp solution, should be handled via admin
const THE_BIG_LEAGUES_IMAGE_PATH = '/partner/the-big-leagues/1.webp'

function DynamicHeader({
  pageTheme,
  shouldShowBox = true,
  leagues,
  shouldShowLeagueDropdown = true,
  isMiniLeague,
  game: gameProp,
}: DynamicHeaderProps) {
  const router = useRouter()
  const [miniLeague] = useMiniLeagueStore((state) => [state.currentMiniLeague])
  const [parentGame] = useParentGameStore((state) => [state.parentGame])
  const [isGameInfoOpen, setIsGameInfoOpen] = React.useState(false)
  const { isAuthenticated } = useAuth()
  const targetLeague = useMemo(() => {
    const leagueId = parseInt(router.query.id as string, 10)
    return leagues?.find((l) => l.id === leagueId)
  }, [router, leagues])
  const shouldShowLoggedOutMenu =
    !isAuthenticated && Boolean(pageTheme?.feature_flags?.public_prize_how_to_play)

  const renderHeaderAction = () => {
    if (!isAuthenticated) {
      if (shouldShowLoggedOutMenu) {
        return <LoggedOutHeaderMenu pageTheme={pageTheme} />
      }
      return <LoginButton />
    }
    return <ProfileDrawer pageTheme={pageTheme} />
  }

  const randomBgImage = useMemo((): string => {
    return (
      targetLeague?.image_url ??
      gameProp?.image_url ??
      pageTheme.game?.image_url ??
      parentGame?.image_url ??
      pageTheme.game?.game_config.sport?.image_url ??
      pageTheme.landing_image ??
      getBgImageBySport(parentGame?.game_config?.sport?.name) ??
      ''
    )
  }, [router, gameProp, parentGame, pageTheme, targetLeague])

  // pageTheme.is_user_joined is stale SSR-computed data once the user logs
  // out mid-session — require the live auth state too.
  const isUserJoined = Boolean(pageTheme?.is_user_joined) && isAuthenticated

  const memoizedGame = useMemo(
    () => gameProp ?? (isMiniLeague ? miniLeague : parentGame),
    [gameProp, isMiniLeague, miniLeague, parentGame],
  )

  const headerLogo = targetLeague?.logo || memoizedGame?.logo || pageTheme.logo

  const headerImageBgOpacity = useMemo(() => {
    if (parentGame?.game_config.sport?.opacity) {
      return parentGame?.game_config.sport.opacity
    }

    if (pageTheme.landing_image_opacity) {
      return pageTheme.landing_image_opacity
    }

    return 0.4 // default to 40%
  }, [targetLeague, gameProp, parentGame])

  const headerImageFilter = useMemo(() => {
    if (targetLeague || (gameProp ?? parentGame)?.image_filter !== GameImageFilter.BW) {
      return ''
    }

    return 'grayscale(100%)'
  }, [targetLeague, parentGame?.image_filter])

  const hasMultipleRounds = (parentGame?.rounds?.length ?? 0) > 1
  const nextUpcomingRound = parentGame ? getFirstUpcomingRound(parentGame) : null
  const nextRoundStartTime = hasMultipleRounds ? nextUpcomingRound?.start_date ?? null : null

  const headerBackgroundStyle = {
    filter: headerImageFilter,
    position: 'absolute' as const,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    opacity: headerImageBgOpacity,
  }

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {randomBgImage && !pageTheme.feature_flags.is_star_sports_page && (
        <img alt="imagery" src={randomBgImage} style={headerBackgroundStyle} />
      )}
      <AppBar
        position="static"
        sx={{
          backgroundColor: pageTheme.header_color,
          boxShadow: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              width: '100%',
              maxWidth: '100vw',
              boxSizing: 'border-box',
              py: { xs: 1.5, sm: 2 },
              px: { xs: '16px', sm: 3 },
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              minHeight: { xs: '84px', sm: '104px', md: '120px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
                minWidth: 0,
              }}
            >
              <HeaderHomeButton
                href={`/${pageTheme.company_domain}/landing`}
                color={pageTheme.game_page_first_header_color}
                borderColor="#DDE6F1"
                isDark={isTBLPage(pageTheme)}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden',
                minWidth: 0,
                maxWidth: { xs: '55vw', sm: '50vw' },
                px: 1,
                zIndex: 1,
              }}
            >
              {headerLogo && (
                <Link href={`/${pageTheme.company_domain}/landing`} style={{ minWidth: 0 }}>
                  <Box
                    component="img"
                    src={headerLogo}
                    alt="Logo"
                    sx={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: { xs: '72px', sm: '88px', md: '96px' },
                      objectFit: 'contain',
                    }}
                  />
                </Link>
              )}
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                zIndex: 1,
                minWidth: 0,
              }}
            >
              <Grid xs={4} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  {renderHeaderAction()}
                </Box>
              </Grid>
            </Box>
          </Box>
        </Container>
      </AppBar>

      <AppBar
        position="static"
        elevation={0}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: pageTheme.header_color,
          p: 0,
        }}
      >
        <Toolbar
          sx={{
            textAlign: 'center',
            marginBottom: '20px',
            mb: '40px',
            width: '100%',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <LeagueCarousel
            page={pageTheme}
            leagues={leagues ?? []}
            isUserJoined={isUserJoined}
            shouldShowLeagueDropdown={shouldShowLeagueDropdown}
            game={memoizedGame}
          />

          {shouldShowBox && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                maxWidth: { xs: '358px', md: '980px' },
                mx: 'auto',
                mt: 2,
                textAlign: 'left',
              }}
            >
              <GameInfoCard
                page={pageTheme}
                game={parentGame}
                onToggleGameInfo={setIsGameInfoOpen}
                hasMultipleRounds={hasMultipleRounds}
                nextRoundStartTime={nextRoundStartTime}
              />
            </Box>
          )}
          {shouldShowBox && (
            <Box
              sx={{
                display: 'flex',
                position: 'absolute',
                width: '100%',
                maxWidth: { xs: '358px', md: '980px' },
                left: '50%',
                transform: 'translateX(-50%)',
                mt: 2,
                top: '100%',
                alignItems: 'center',
                borderRadius: '12px',
                justifyContent: 'space-between',
              }}
            >
              <SectionSwitcher
                pageTheme={pageTheme}
                game={parentGame}
                disableSticky={isGameInfoOpen}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  )
}

export default DynamicHeader
